import { supabase } from '@/integrations/supabase/client';
import type { SavedScenarioSet, SaveScenarioParams, LoadScenarioResult, ScenarioSetListItem } from '@/types/supremeIntelligenceTypes';

const STORAGE_KEY_PREFIX = 'equiforge-saved-scenarios';

export class ScenarioStorageService {
  /**
   * Save a scenario set to Supabase (if authenticated) and localStorage
   */
  async saveScenarioSet(params: SaveScenarioParams, userId?: string): Promise<SavedScenarioSet> {
    const scenarioSet: Partial<SavedScenarioSet> = {
      userId: userId || null,
      name: params.name,
      description: params.description,
      scenarios: params.scenarios,
      formData: params.formData,
      metadata: {
        tags: params.tags || [],
        version: '1.0.0',
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      lastAccessedAt: new Date().toISOString(),
    };

    // Save to Supabase if authenticated
    if (userId) {
      const { data, error } = await supabase
        .from('saved_scenarios')
        .insert([{
          user_id: userId,
          name: params.name,
          description: params.description,
          scenarios: params.scenarios as any,
          form_data: params.formData as any,
          metadata: scenarioSet.metadata as any,
        }])
        .select()
        .single();

      if (error) {
        console.error('Failed to save to Supabase:', error);
        throw new Error(`Failed to save scenario set: ${error.message}`);
      }

      const savedSet: SavedScenarioSet = {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        description: data.description,
        scenarios: data.scenarios as any,
        formData: data.form_data,
        metadata: data.metadata as any,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        lastAccessedAt: data.last_accessed_at,
      };

      // Also cache in localStorage
      this.saveToLocalStorage(savedSet);

      return savedSet;
    } else {
      // Save to localStorage only for anonymous users
      const id = crypto.randomUUID();
      const completeSet: SavedScenarioSet = {
        ...scenarioSet as SavedScenarioSet,
        id,
      };
      this.saveToLocalStorage(completeSet);
      return completeSet;
    }
  }

  /**
   * Update an existing scenario set
   */
  async updateScenarioSet(id: string, updates: Partial<SaveScenarioParams>): Promise<SavedScenarioSet> {
    const { data: session } = await supabase.auth.getSession();
    const userId = session.session?.user?.id;

    if (userId) {
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      if (updates.name) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.scenarios) updateData.scenarios = updates.scenarios;
      if (updates.formData) updateData.form_data = updates.formData;
      if (updates.tags) updateData.metadata = { tags: updates.tags, version: '1.0.0' };

      const { data, error } = await supabase
        .from('saved_scenarios')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update scenario set: ${error.message}`);
      }

      const savedSet: SavedScenarioSet = {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        description: data.description,
        scenarios: data.scenarios as any,
        formData: data.form_data,
        metadata: data.metadata as any,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        lastAccessedAt: data.last_accessed_at,
      };

      this.saveToLocalStorage(savedSet);
      return savedSet;
    } else {
      const existing = this.loadFromLocalStorage(id);
      if (!existing) {
        throw new Error('Scenario set not found');
      }

      const updated: SavedScenarioSet = {
        ...existing,
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      this.saveToLocalStorage(updated);
      return updated;
    }
  }

  /**
   * Load a scenario set by ID
   */
  async loadScenarioSet(id: string): Promise<LoadScenarioResult | null> {
    const { data: session } = await supabase.auth.getSession();
    const userId = session.session?.user?.id;

    if (userId) {
      // Try to load from Supabase
      const { data, error } = await supabase
        .from('saved_scenarios')
        .select()
        .eq('id', id)
        .single();

      if (error) {
        console.warn('Failed to load from Supabase, trying localStorage:', error);
        const localData = this.loadFromLocalStorage(id);
        if (localData) {
          return {
            scenarios: localData.scenarios,
            formData: localData.formData,
            metadata: localData.metadata,
          };
        }
        return null;
      }

      // Update last accessed timestamp
      await this.updateLastAccessed(id);

      return {
        scenarios: data.scenarios as any,
        formData: data.form_data,
        metadata: data.metadata as any,
      };
    } else {
      // Load from localStorage for anonymous users
      const localData = this.loadFromLocalStorage(id);
      if (!localData) return null;

      return {
        scenarios: localData.scenarios,
        formData: localData.formData,
        metadata: localData.metadata,
      };
    }
  }

  /**
   * List all scenario sets for the current user
   */
  async listScenarioSets(userId?: string): Promise<ScenarioSetListItem[]> {
    if (userId) {
      const { data, error } = await supabase
        .from('saved_scenarios')
        .select('id, name, description, scenarios, created_at, updated_at, last_accessed_at, metadata')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Failed to list from Supabase:', error);
        return this.listFromLocalStorage();
      }

      return data.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description,
        scenarioCount: Array.isArray(item.scenarios) ? item.scenarios.length : 0,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        lastAccessedAt: item.last_accessed_at,
        tags: (item.metadata as any)?.tags || [],
      }));
    } else {
      return this.listFromLocalStorage();
    }
  }

  /**
   * Delete a scenario set
   */
  async deleteScenarioSet(id: string): Promise<boolean> {
    const { data: session } = await supabase.auth.getSession();
    const userId = session.session?.user?.id;

    if (userId) {
      const { error } = await supabase
        .from('saved_scenarios')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Failed to delete from Supabase:', error);
        return false;
      }
    }

    // Also remove from localStorage
    this.removeFromLocalStorage(id);
    return true;
  }

  /**
   * Duplicate a scenario set
   */
  async duplicateScenarioSet(id: string, newName: string): Promise<SavedScenarioSet> {
    const existing = await this.loadScenarioSet(id);
    if (!existing) {
      throw new Error('Scenario set not found');
    }

    const { data: session } = await supabase.auth.getSession();
    const userId = session.session?.user?.id;

    return this.saveScenarioSet(
      {
        name: newName,
        description: existing.metadata.source ? `Copy of ${newName}` : undefined,
        scenarios: existing.scenarios,
        formData: existing.formData,
        tags: existing.metadata.tags,
      },
      userId
    );
  }

  /**
   * Update last accessed timestamp
   */
  async updateLastAccessed(id: string): Promise<void> {
    const { data: session } = await supabase.auth.getSession();
    const userId = session.session?.user?.id;

    if (userId) {
      await supabase
        .from('saved_scenarios')
        .update({ last_accessed_at: new Date().toISOString() })
        .eq('id', id);
    }
  }

  /**
   * Sync localStorage scenarios to Supabase after user login
   */
  async syncLocalToCloud(userId: string): Promise<number> {
    const localScenarios = this.listFromLocalStorage();
    let syncedCount = 0;

    for (const item of localScenarios) {
      const fullData = this.loadFromLocalStorage(item.id);
      if (!fullData) continue;

      try {
        await this.saveScenarioSet(
          {
            name: fullData.name,
            description: fullData.description,
            scenarios: fullData.scenarios,
            formData: fullData.formData,
            tags: fullData.metadata.tags,
          },
          userId
        );
        syncedCount++;
      } catch (error) {
        console.error(`Failed to sync scenario ${item.id}:`, error);
      }
    }

    return syncedCount;
  }

  // Private localStorage methods

  private getStorageKey(id?: string): string {
    return id ? `${STORAGE_KEY_PREFIX}-${id}` : `${STORAGE_KEY_PREFIX}-list`;
  }

  private saveToLocalStorage(data: SavedScenarioSet): void {
    try {
      localStorage.setItem(this.getStorageKey(data.id), JSON.stringify(data));
      
      // Update the list
      const list = this.listFromLocalStorage();
      const existingIndex = list.findIndex(item => item.id === data.id);
      
      const listItem: ScenarioSetListItem = {
        id: data.id,
        name: data.name,
        description: data.description,
        scenarioCount: data.scenarios.length,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        lastAccessedAt: data.lastAccessedAt,
        tags: data.metadata.tags,
      };

      if (existingIndex >= 0) {
        list[existingIndex] = listItem;
      } else {
        list.push(listItem);
      }

      localStorage.setItem(this.getStorageKey(), JSON.stringify(list));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }

  private loadFromLocalStorage(id: string): SavedScenarioSet | null {
    try {
      const data = localStorage.getItem(this.getStorageKey(id));
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
      return null;
    }
  }

  private listFromLocalStorage(): ScenarioSetListItem[] {
    try {
      const data = localStorage.getItem(this.getStorageKey());
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to list from localStorage:', error);
      return [];
    }
  }

  private removeFromLocalStorage(id: string): void {
    try {
      localStorage.removeItem(this.getStorageKey(id));
      
      const list = this.listFromLocalStorage();
      const filtered = list.filter(item => item.id !== id);
      localStorage.setItem(this.getStorageKey(), JSON.stringify(filtered));
    } catch (error) {
      console.error('Failed to remove from localStorage:', error);
    }
  }
}

export const scenarioStorage = new ScenarioStorageService();
